"use client";

import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { BaseLink } from "@ui/components/core/Link";
import { clsx } from "clsx";
import Image from "next/image";
import type { Media } from "@mw/shared/payload-types";
import { getImageProps } from "@utilities/payload";
import { pxToRem } from "@ui/lib/utils";
import IconChevronRightMedium from "@ui/central-icons/icons/IconChevronRightMedium";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActionsItemAsset = { type: "icon"; icon: React.ReactNode } | { type: "image"; image: Media };

type ActionsItemProps =
  | {
      title: string;
      asset: ActionsItemAsset;
      href: string;
      onClick?: never;
      assetStyle?: "default" | "transparent";
      hoverStyle?: "default" | "brighten";
    }
  | {
      title: string;
      asset: ActionsItemAsset;
      href?: never;
      onClick: React.ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
      assetStyle?: "default" | "transparent";
      hoverStyle?: "default" | "brighten";
    }
  | {
      title: string;
      asset: ActionsItemAsset;
      href?: never;
      onClick?: never;
      assetStyle?: "default" | "transparent";
      hoverStyle?: "default" | "brighten";
    };

type ActionsListProps = {
  items: ActionsItemProps[];
  className?: string;
  assetStyle?: "default" | "transparent";
  hoverStyle?: "default" | "brighten";
};

// ---------------------------------------------------------------------------
// Asset style presets
// ---------------------------------------------------------------------------

const assetStyleConfig = {
  default: {
    size: "tw-size-7",
    sizes: pxToRem(28),
    background: "tw-bg-gray-400 tw-rounded-[0.625rem]",
    ring: "tw-rounded-[0.625rem] tw-border-0.5 tw-border-black/15",
    iconContainer:
      "tw-bg-gray-400 group-hover:tw-bg-blue-700 tw-transition-colors tw-duration-150 tw-size-full tw-flex tw-justify-center tw-items-center",
    iconColor: "tw-text-white",
    iconSize: "tw-size-4.5",
    showRing: true,
  },
  transparent: {
    size: "tw-size-7",
    sizes: pxToRem(28),
    background: "tw-bg-transparent tw-rounded-[0.625rem]",
    ring: "",
    iconContainer:
      "tw-bg-transparent tw-transition-colors tw-duration-150 tw-size-full tw-flex tw-justify-center tw-items-center",
    iconColor: "tw-text-black",
    iconSize: "tw-size-4.5",
    showRing: false,
  },
} as const;

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function AssetSlot({ asset, style }: { asset: ActionsItemAsset; style: "default" | "transparent" }) {
  const config = assetStyleConfig[style];

  const imageProps = asset.type === "icon" ? undefined : getImageProps(asset.image);

  return (
    <span
      className={clsx(
        "tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden",
        config.background,
        config.size,
      )}
    >
      {asset.type === "icon" && (
        <span className={config.iconContainer}>
          <span className={clsx("tw-flex tw-items-center tw-justify-center", config.iconColor, config.iconSize)}>
            {asset.icon}
          </span>
        </span>
      )}

      {imageProps && (
        <Image {...imageProps} alt='' className={clsx(config.size, "tw-object-cover")} sizes={config.sizes} />
      )}

      {config.showRing && <div className={clsx("tw-absolute tw-inset-0", config.ring)} />}
    </span>
  );
}

const hoverStyleConfig = {
  default: {
    text: "tw-text-black",
  },
  brighten: {
    text: "tw-text-black/60 group-hover:tw-text-black tw-transition-colors tw-duration-150",
  },
} as const;

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export function ActionsItem({
  title,
  asset,
  assetStyle = "default",
  hoverStyle = "default",
  ...rest
}: ActionsItemProps) {
  const hoverConfig = hoverStyleConfig[hoverStyle];
  const isInteractive = Boolean(rest.href || rest.onClick);

  const inner = (
    <>
      <AssetSlot asset={asset} style={assetStyle} />
      <span className='tw-type-xs-tight tw-my-auto'>
        <span
          className={clsx("tw-inline-block tw-relative", hoverConfig.text)}
          style={{ paddingRight: isInteractive ? pxToRem(22) : undefined }}
        >
          {title}
          {isInteractive && (
            <IconChevronRightMedium
              className={clsx(
                iconStrokeFix,
                "tw-absolute tw-inline tw-ml-1.5",
                "tw-size-4 tw-text-black",
                "tw-opacity-0 -tw-translate-x-1 tw-transition tw-ease-out",
                "group-hover:tw-opacity-100 group-hover:tw-translate-x-0 group-focus-visible:tw-opacity-100 group-focus-visible:tw-translate-x-0",
              )}
              style={{ marginTop: pxToRem(1.5) }}
            />
          )}
        </span>
      </span>
    </>
  );

  const sharedClassName = "tw-group tw-flex tw-gap-2 tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm";

  if (rest.href) {
    return (
      <BaseLink href={rest.href} className={sharedClassName}>
        {inner}
      </BaseLink>
    );
  }

  if (rest.onClick) {
    return (
      <button
        type='button'
        onClick={rest.onClick}
        className={clsx(sharedClassName, "tw-cursor-pointer tw-bg-transparent")}
      >
        {inner}
      </button>
    );
  }

  return <span className='tw-flex tw-items-center tw-gap-2'>{inner}</span>;
}

export function ActionsList({ items, className, assetStyle = "default", hoverStyle }: ActionsListProps) {
  return (
    <div className={clsx("tw-flex tw-flex-col tw-gap-3", className)}>
      {items.map((item, i) => (
        <ActionsItem key={i} assetStyle={assetStyle} hoverStyle={hoverStyle} {...item} />
      ))}
    </div>
  );
}

export type { ActionsItemProps, ActionsItemAsset, ActionsListProps };
